import { useEffect, useMemo } from 'react';
import { useSession } from 'next-auth/react';
import { useSuspenseInfiniteQuery } from '@tanstack/react-query';

import type { Session } from 'next-auth';
import type { InfiniteData } from '@tanstack/react-query';

import { conversationsOptions } from '~/features/chat/querries';

import type { IConversationVM, TListResponse } from '~/types';

export function useChatNotification() {
  const { data: session } = useSession();
  const { data, refetch } = useSuspenseInfiniteQuery(conversationsOptions());
  const realtimeNotifications = useMemo(
    () => processingListConversationToChatNotification(data, session),
    [data, session],
  );

  const haveUnreadChat = useMemo(() => {
    return realtimeNotifications.some((item) => !item.read);
  }, [realtimeNotifications]);

  useEffect(() => {
    if (!session?.user?.id || !session?.accessToken) return;

    const queryParams = new URLSearchParams({
      uid: session.user.email,
      token: session.accessToken,
      user_id: session.user.id.toString(),
    });

    const ws = new WebSocket(
      `${process.env.NEXT_PUBLIC_WEBSOCKET_URL}?${queryParams.toString()}`,
    );

    ws.onopen = () => {
      ws.send(
        JSON.stringify({
          command: 'subscribe',
          identifier: JSON.stringify({
            channel: 'UserConversationsChannel',
            user_id: session.user.id,
          }),
        }),
      );
    };

    ws.onmessage = (e) => {
      try {
        const data = JSON.parse(e.data);
        if (typeof data.message === 'object') {
          refetch();
        }
      } catch {}
    };

    return () => {
      ws.close();
    };
  }, [session?.user?.id, session?.accessToken, session?.user?.email, refetch]);

  return {
    realtimeNotifications,
    haveUnreadChat,
  };
}

function processingListConversationToChatNotification(
  data: InfiniteData<TListResponse<IConversationVM>>,
  session: Session | null,
): { id: string; message: string; createdAt: string; read: boolean }[] {

  try {
    return data.pages
      .flatMap(([convo]) => convo)
      .map((item) => ({
        ...item,
        participants: item.participants.filter(
          (participant) => participant.user.id !== session?.user.id,
        ),
      }))
      .filter(({ participants }) => participants.length > 0)
      .map(({ lastMessage, participants, ...rest }) => ({
        ...rest,
        sender: participants[0].user,
        lastMessage,
        isUnread:
          lastMessage &&
          (!lastMessage.lastSeenAt ||
            lastMessage.lastSeenAt < lastMessage.updatedAt) &&
          lastMessage.content &&
          lastMessage.sender.id !== session?.user.id,
        isYourLastMessage: lastMessage?.sender.id === session?.user.id,
      }))
      .filter(
        ({ lastMessage, isYourLastMessage }) =>
          !!lastMessage && !isYourLastMessage,
      )
      .map((data) => ({
        id: data.id,
        message: getChatNotificationMessage(data.sender.fullName),
        createdAt: data.lastMessage?.updatedAt,
        read: !data.isUnread,
      }));
  } catch (error) {
    console.error(error)
    return [];
  }
}

function getChatNotificationMessage(fullName: string): string {
  return `You have a new message from ${fullName}`;
}
